// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import React, { useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    product: CartLineModel | ProductContextModel;
    onClick?: React.EventHandler<React.MouseEvent>;
    extendedStyles?: ProductImageStyles;
    showSmallImage?: boolean;
}

type Props = OwnProps;

export interface ProductImageStyles {
    linkWrappingImage?: ClickablePresentationProps;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
}

export const productImageStyles: ProductImageStyles = {
    linkWrappingImage: {
        css: css`
            width: 100%;
        `,
    },
    image: {
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    img: {
        css: css`
            height: auto;
            width: 100%;
        `,
    },
};

const ProductImage = ({ product, onClick, extendedStyles, showSmallImage }: Props) => {
    const [styles] = useState(() => mergeToNew(productImageStyles, extendedStyles));

    let altText = "product" in product ? product.product.imageAltText : product.altText;
    if (!altText || altText === "") {
        altText = "product" in product ? product.product.productTitle : product.productName;
    }

    const imagePath =
        "product" in product
            ? showSmallImage
                ? product.product.smallImagePath || product.product.mediumImagePath
                : product.product.mediumImagePath || product.product.smallImagePath
            : product.smallImagePath;

    const productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;
    const loading = product.idx === undefined ? "lazy" : product.idx < 3 ? "eager" : "lazy";

    /**
     * XNG-Changes
     * Allow Pass width/height by extensions
     */
    return (
        <Clickable
            {...styles.linkWrappingImage}
            href={productDetailPath}
            onClick={onClick}
            data-test-selector="productImage"
        >
            <Img loading={loading} width="209" height="209" src={imagePath} altText={altText} {...styles.img} />
        </Clickable>
    );
};

export default ProductImage;
